'use client';

import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { GiftCouponValue } from '@eliadmin/core/src/components/product/gift-coupon-value';
import { getGiftItemDisplay } from '@eliadmin/core/src/utils/cart-gift-utils';
import type { ProductGift } from '@eliadmin/core/src/api/products/types/product-types';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { giftPackageLabel } from '@eliadmin/core/src/i18n/gift-labels';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { MultiTierGift } from '@eliadmin/core/src/components/product/detail/multi-tier-gift';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import type { ReactNode } from 'react';

function GiftProductLink({ slug, children, className }: { slug: string | null; children: ReactNode; className?: string }) {
  return slug ? (
    <Link href={ROUTES.PRODUCT({ slug })} prefetch={false} className={cn('hover:underline underline-offset-2', className)}>
      {children}
    </Link>
  ) : <span className={className}>{children}</span>;
}

/** Keep the original one-reward presentation, reading only the current tier DTO. */
function SingleTierGift({ gift }: { gift: ProductGift }) {
  const tier = gift.tiers[0];
  const { t } = useTranslations();
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const locale = useLocaleStore(s => s.currentLocale);
  const minimum = tier.order_price === null ? null : Number(tier.order_price);
  return (
    <div data-gift-id={gift.id} data-gift-tier-id={tier.id} data-gift-presentation="single"
      className="ea-product-detail-gifts__item w-full rounded-lg border border-border bg-white px-4 py-2 shadow-sm">
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-row items-center justify-between gap-2">
          <h3 className="ea-product-detail-header__title mb-0 text-sm font-semibold">{gift.name}</h3>
          {minimum !== null && Number.isFinite(minimum) && (
            <span className="ea-product-detail-gifts__item-price mb-0 whitespace-nowrap text-xs font-bold uppercase md:text-sm">
              {minimum === 0 ? t('text3230') : `${t('text3229')} ${formatPrice(minimum, currency, currencySettings).replace(/([.,])0+(\s|$)/, '$2')}`}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2" title={giftPackageLabel(locale)}>
          {tier.reward_coupon && (
            <div className="ea-product-detail-gifts__coupon flex flex-row items-center gap-2">
              <GiftCouponValue coupon={tier.reward_coupon} className="ea-product-detail-gifts__coupon-value" />
              <span className="ea-product-detail-gifts__coupon-name text-sm font-semibold">{tier.reward_coupon.name}</span>
            </div>
          )}
          {tier.items.map(item => {
            const display = getGiftItemDisplay(item);
            const priceSource = item.variant ?? item.product;
            const price = priceSource ? getProductPriceDisplay(priceSource) : null;
            return (
              <div key={item.id} className="flex flex-row items-center gap-2">
                <GiftProductLink slug={display.href} className="shrink-0">
                <Image src={display.image?.url ?? '/images/placeholder.png'} alt={resolveImageAlt(display.image, display.name, gift.name)}
                  width={72} height={72} sizes="72px" className="h-[72px] w-[72px] rounded-md object-cover" />
                </GiftProductLink>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">{Number(display.quantity) !== 1 && <>{display.quantity} × </>}<GiftProductLink slug={display.href}>{display.name}</GiftProductLink></span>
                  {price && !price.priceOnRequest && <span className="text-sm text-muted-foreground">{t('text3228')} {formatPrice(price.current, currency, currencySettings)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Product tiers are alternative previews within a rule, never a cart entitlement. */
export const ProductGifts = ({
  multiTierHeading = 'label',
  multiTierHeadingClassName,
}: {
  multiTierHeading?: 'label' | 'name';
  multiTierHeadingClassName?: string;
} = {}) => {
  const product = useVariantsStore(s => s.product)!;
  const { t } = useTranslations();
  if (!product.gifts?.length) return null;

  return (
    <div className={cn('flex w-full flex-col items-start gap-3', 'ea-product-detail-gifts')}>
      {product.gifts.some(gift => gift.tiers.length === 1) && (
        <h2 className="ea-product-detail-header__title mb-1 text-sm font-bold">{t('text664')}</h2>
      )}
      {product.gifts.map(gift => {
        if (gift.tiers.length === 1) return <SingleTierGift key={gift.id} gift={gift} />;
        if (!gift.tiers.length) return null;
        return (
          <MultiTierGift
            key={gift.id}
            gift={gift}
            heading={multiTierHeading === 'name' ? gift.name : t('text664')}
            headingClassName={multiTierHeadingClassName}
          />
        );
      })}
    </div>
  );
};
